'use client';

import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { type ComponentProps, useCallback, useEffect, useState } from 'react';
import { prefetchOnHover } from '@/lib/graphql-prefetch';

type SmartLinkProps = ComponentProps<typeof Link> & {
    prefetch?: boolean; // Override default prefetch behavior
};

export function SmartLink({
    children,
    href,
    prefetch = true,
    onClick,
    target,
    ...props
}: SmartLinkProps) {
    const queryClient = useQueryClient();
    const router = useRouter();
    // Track if component has mounted to avoid hydration issues with event handlers
    const [hasMounted, setHasMounted] = useState(false);

    useEffect(() => {
        setHasMounted(true);
    }, []);

    // Prefetch on hover for instant navigation
    const handleMouseEnter = useCallback(() => {
        if (!hasMounted) return;
        if (prefetch && typeof href === 'string') {
            // Prefetch the route
            router.prefetch(href);

            // Prefetch GraphQL data
            prefetchOnHover(queryClient, href);
        }
    }, [href, prefetch, queryClient, router, hasMounted]);

    // Handle click
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            // Call original onClick if provided
            if (onClick) {
                onClick(e);
            }
            // Let Next.js Link handle navigation normally
        },
        [onClick]
    );

    return (
        <Link
            href={href}
            target={target}
            {...props}
            onMouseEnter={hasMounted ? handleMouseEnter : undefined}
            onClick={handleClick}
            // Disable Next.js default prefetch since we handle it manually
            prefetch={false}
        >
            {children}
        </Link>
    );
}
